import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Package, Plus, Minus, X, AlertTriangle, CheckCircle, Info } from 'lucide-react';

const EquipmentSelector = ({
  availableEquipment,
  selectedEquipment,
  availabilityWarnings,
  checkingAvailability,
  hasDates,
  onAddEquipment,
  onRemoveEquipment,
  onQuantityChange,
  testIdPrefix = ''
}) => {
  const getEquipmentById = (id) => availableEquipment.find(eq => eq.id === id);
  
  const equipmentDailyTotal = selectedEquipment.reduce((sum, item) => {
    const eq = getEquipmentById(item.id);
    return sum + ((eq?.price || 0) * item.quantity);
  }, 0);

  const totalEquipmentItems = selectedEquipment.reduce((sum, item) => sum + item.quantity, 0);

  const getAvailabilityIcon = (warning) => {
    if (!warning) return null;
    if (warning.is_available) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    return <AlertTriangle className="w-4 h-4 text-amber-500" />;
  };

  return (
    <div className="border-t pt-4 mt-4">
      <Label className="flex items-center text-base font-semibold mb-3">
        <Package className="w-4 h-4 mr-2 text-blue-600" />
        Project Apparatuur
        {equipmentDailyTotal > 0 && (
          <span className="ml-auto text-sm font-normal text-green-600">
            €{equipmentDailyTotal.toFixed(2)}/dag
          </span>
        )}
      </Label>
      
      {!hasDates && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 flex items-center">
          <Info className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" />
          <p className="text-sm text-blue-700">Selecteer eerst projectdatums om beschikbaarheid te controleren</p>
        </div>
      )}
      
      <div className="mb-3">
        <Select onValueChange={onAddEquipment}>
          <SelectTrigger data-testid={`${testIdPrefix}equipment-select-trigger`}>
            <SelectValue placeholder="Selecteer apparatuur om toe te voegen..." />
          </SelectTrigger>
          <SelectContent>
            {availableEquipment
              .filter(eq => !selectedEquipment.find(s => s.id === eq.id))
              .map(eq => (
                <SelectItem key={eq.id} value={eq.id}>
                  <div className="flex items-center justify-between w-full">
                    <span>{eq.name}</span>
                    <span className="text-xs text-gray-500 ml-2">
                      ({eq.category}) - €{eq.price}/dag • {eq.available} op voorraad
                    </span>
                  </div>
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      {selectedEquipment.length > 0 ? (
        <div className="bg-blue-50 rounded-lg p-3 space-y-2">
          <p className="text-sm font-medium text-blue-700 mb-2">
            Geselecteerde Apparatuur ({totalEquipmentItems} items)
            {checkingAvailability && <span className="ml-2 text-gray-500">(beschikbaarheid controleren...)</span>}
          </p>
          {selectedEquipment.map(item => {
            const eq = getEquipmentById(item.id);
            const warning = availabilityWarnings[item.id];
            return eq ? (
              <div key={item.id} className="space-y-1">
                <div 
                  className={`flex items-center justify-between bg-white rounded-lg p-2 border ${
                    warning && !warning.is_available ? 'border-amber-300' : 'border-blue-200'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center">
                      {getAvailabilityIcon(warning)}
                      <span className="font-medium text-gray-900 ml-1">{eq.name}</span>
                    </div>
                    <span className="text-sm text-gray-500">
                      €{eq.price}/dag × {item.quantity} = €{(eq.price * item.quantity).toFixed(2)}/dag
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => onQuantityChange(item.id, -1)}
                      disabled={item.quantity <= 1}
                    >
                      <Minus className="w-3 h-3" />
                    </Button>
                    <span className="w-8 text-center font-semibold">{item.quantity}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => onQuantityChange(item.id, 1)}
                      disabled={item.quantity >= item.maxQuantity}
                    >
                      <Plus className="w-3 h-3" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                      onClick={() => onRemoveEquipment(item.id)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                {/* Beschikbaarheid Waarschuwing */}
                {warning && !warning.is_available && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-2 ml-2">
                    <div className="flex items-start">
                      <AlertTriangle className="w-4 h-4 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                      <div className="text-xs">
                        <p className="font-medium text-amber-800">{warning.message}</p>
                        {warning.conflicts && warning.conflicts.length > 0 && (
                          <div className="mt-1 text-amber-700">
                            <p>Conflicterende boekingen:</p>
                            <ul className="list-disc ml-4">
                              {warning.conflicts.map((c, i) => (
                                <li key={i}>
                                  {c.project_name} ({c.start_date} - {c.end_date}) - {c.quantity_booked} stuks
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div 
                key={item.id} 
                className="flex items-center justify-between bg-gray-50 rounded-lg p-2 border border-gray-200"
              >
                <span className="text-gray-500">{item.id} (niet gevonden)</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                  onClick={() => onRemoveEquipment(item.id)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-3 bg-gray-50 rounded-lg">
          <Package className="w-6 h-6 text-gray-300 mx-auto mb-1" />
          <p className="text-sm text-gray-500">Geen apparatuur toegevoegd</p>
        </div>
      )}
    </div>
  );
};

export default EquipmentSelector;
